// Screen-level UI state (Zustand). Live game values never go here; they stay in the World store.
import { create } from 'zustand';
import type { ParkSession } from '../game/session';
import type { AccountKind, ApiConfig, Me } from '../lib/api';
import type { Director } from '../practice/director';
import { S } from '../strings';
import type { ParkLoadInfo } from '../ui/parkLoad';

/** Notices that report no fault: shown on the board, never in wahala red (05-money, Toast README). */
const CALM = new Set(['cancelled', 'slow_down', 'rate_limited', 'busy'].map((k) => S.errors[k]));

export type Screen =
  | 'boot'
  | 'outside'
  | 'login'
  | 'menu'
  | 'park'
  | 'shop'
  | 'pass'
  | 'today'
  | 'board'
  | 'history'
  | 'profile'
  | 'people'
  | 'compete'
  | 'chat';

export interface StakeTable {
  id: string;
  code: string;
  status: 'open' | 'playing' | 'done' | 'cancelled' | 'void';
  seats: number;
  seated: number;
  /** The fare each seat put in (Lagos Life's word). */
  stake: number;
}

/** A stake table that never filled: its fare is back, and a free round is on offer (PF-07). */
export interface StakeOffer {
  game: 'park' | 'race';
  stake: number;
}

interface Ui {
  screen: Screen;
  config: ApiConfig | null;
  me: Me | null;
  /** The picture Lagos Life gave us for this player (never stored on our server); null outside it. */
  avatar: string | null;
  /**
   * Whose account this is (PF-19): a website guest (who may save their profile), a saved website
   * account, or a distributor's player (never offered any of it); null until the server says.
   */
  account: AccountKind | null;
  park: ParkSession | null;
  /** The tutorial running in the practice park, if any. */
  practice: Director | null;
  notice: string | null;
  /** Good news reads as done; anything else as a problem. Reset with each new notice. */
  /** done (lagoon) for good news, info (board) for neutral news, wahala only for a real problem. */
  noticeTone: 'done' | 'info' | 'wahala';
  /** My coin balance as the server last said; null until it has been asked. */
  coins: number | null;
  /** The Lagos Life stake table this phone sat at, as its last update said (PF-07); null otherwise. */
  stakeTable: StakeTable | null;
  stakeOffer: StakeOffer | null;
  /** Get the app: the install sheet is open (the Menu, or the website's ?install=1). */
  installSheet: boolean;
  /** The park on the way in, as far as it is known (ui/parkLoad.ts): its game, code, this player's keke. */
  entering: ParkLoadInfo | null;
  /** Asking for a seat (the park office, or Solo's kit arriving): the way-in screen covers the menu. */
  joining: boolean;
  /** The leave rules: when my wait to join a park with people ends (this phone's clock), shown as its own board. */
  cooldownUntil: number | null;
  /** A sheet the Play menu should open as it shows (the cooldown board's Solo shortcut). */
  menuTile: 'solo' | null;
  set(p: Partial<Omit<Ui, 'set'>>): void;
}

export const useUi = create<Ui>((set) => ({
  screen: 'boot',
  config: null,
  me: null,
  avatar: null,
  account: null,
  park: null,
  practice: null,
  notice: null,
  noticeTone: 'wahala',
  coins: null,
  stakeTable: null,
  stakeOffer: null,
  installSheet: false,
  entering: null,
  joining: false,
  cooldownUntil: null,
  menuTile: null,
  // a notice set without a tone is a problem, unless it is one of the calm ones (nothing went wrong)
  set: (p) =>
    set(
      'notice' in p && !('noticeTone' in p)
        ? { ...p, noticeTone: p.notice && CALM.has(p.notice) ? 'info' : 'wahala' }
        : p,
    ),
}));
